import { StepFlow } from './StepFlow';
import { FluidSlider } from './Slider';
import { TabList } from './TabList';

class MachineFinder {
	constructor(root) {
		this.root = root;
		this.stepFields = Array.from(document.querySelectorAll('.step-field'));
		this.nextTriggers = this.root.querySelectorAll('[data-next]');

		this.jsonURL = '';
		
		this.init();
	}
	
 	/**
     * Does some light string escaping
     * @param  {String}  string  The string that is to be escaped
     */
    escape(string) {
        return string
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }

	init() {
		this.attachStepFieldOnChange();
		this.currentStepLabels = new Object();
		this.jsonURL = this.root.getAttribute('data-fetch-url');
		this.jsonStep = this.root.getAttribute('data-fetch-step') ?? "1";
		const ids = [...document.querySelectorAll('.is-step')].flatMap(el => el.id ? [el.id] : []);

		if(ids.length === 0) return;

		this.flow = new StepFlow({
			stage: document.querySelector(".machine-finder__stage"),
			templates: ids,
			loadingId: "step-loading",
			onRender: (stage, step, direction) => {
				if (step === "loading") {
					this.fetchJSON.bind(this);
					this.fillStepLabels(stage, step, direction);
					this.initiateLoading(stage, step, direction);
					return;
				}
				this.stepSetup = false;

				this.fillStepLabels(stage, step, direction);
				this.setupPrefill(stage, step, direction);
				this.setupFluidSlider(stage, step, direction);
				this.attachButtonOptionsOnClick(stage, step, direction);
			},
			beforeRender: (step, direction) => {
				if (step === "loading") {
					this.fetchJSON();
					return;
				}
			},
			afterRender: (step, direction, { isNavigating }) => {
				if (!isNavigating) return;
				
				if (window.Trustpilot && window.Trustpilot.loadFromElement) {
					const trustpilotElm = document.getElementById('trustpilot-widget');
					if (trustpilotElm) {
						window.Trustpilot.loadFromElement(trustpilotElm);
					}
				}
				this.root.scrollIntoView();
			}
		});

		this.flow.init();

		for (let i = 0; i < this.nextTriggers.length; i++) {
			this.nextTriggers[i].addEventListener('click', () => this.flow.next());
		}
	}

	setupPrefill(stage, step, direction) {
		const stepField = this.stepFields.find(node => node.classList.contains('step-field-' + step));

		if (stepField.value && stepField.value !== '') {
			let optionButtons = Array.from(stage.querySelectorAll('.machine-finder__option'));

			if (optionButtons) {
				const values = stepField.value.split(";");
				const labels = [];
				
				values.forEach((value) => {
					const selectedOption = optionButtons.find(option => option.dataset.value === value);
					
					if (selectedOption) {
						this.setupNextStepNavigation();
						selectedOption.classList.add('is-selected');
						labels.push(selectedOption.dataset.label); 
					}
				})

				this.currentStepLabels[step] = labels.join(', ');
			}

			const sliderElm = stage.querySelector('.component--slider--js');
			if (sliderElm) {
				this.setupNextStepNavigation();
			}
		}
	}

	fillStepLabels(stage, step, direction) {
		if (!this.currentStepLabels) return;

		const elms = document.querySelectorAll('[data-selection="' + this.flow.getPreviousStep() + '"]');

		if (!this.currentStepLabels[this.flow.getPreviousStep()]) return;

		elms.forEach((elm) => { elm.textContent = this.currentStepLabels[this.flow.getPreviousStep()] });
	}

	attachButtonOptionsOnClick(stage, step, direction) {
		let optionButtons = stage.querySelectorAll('.machine-finder__option');

		if (!optionButtons) return;
		const optionClickHandler = this.onOptionClick.bind(this, stage, step, direction, optionButtons);
		optionButtons.forEach((option) => {
			option.addEventListener("click", optionClickHandler, true);
		});
	}

	setupFluidSlider(stage, step, direction) {
		const tabListElm = stage.querySelector('.tablist--js');
		if (!tabListElm) return;

		new TabList(tabListElm, {});

		const sliderElm = stage.querySelector('.component--slider--js');
		if (!sliderElm) return;

		new FluidSlider(sliderElm, {
			bindTargets: [
				".slider-input",
				".slider-count-display--js",
				".step-field-" + step,
			],
			onChange: (value) => {
				const template = document.querySelector("#step-" + step);
				template.content.querySelector(".component--slider--js").dataset.default = value;
				this.currentStepLabels[step] = value + ' Cups';
			}
		});
	}

	attachStepFieldOnChange() {
		this.stepFields.forEach((input) => {
			input.addEventListener("change", () => {
				if (input.value && input.value !== '') {
					this.setupNextStepNavigation();
				} else {
					this.resetNextStepNavigation();
				}
			}, true);
		});
	}

	setupNextStepNavigation() {
		if (this.stepSetup === true) return;
		const nextBtn = this.root.querySelector("[data-next]");
		nextBtn.removeAttribute('disabled');

		this.nextBtnClickHandler = () => { this.flow.next() };
		this.boundNextBtnClickHandler = this.nextBtnClickHandler.bind(this);

		nextBtn.addEventListener("click", this.boundNextBtnClickHandler, true);

		if (this.flow.getNextStep() > this.flow.getCurrentStep()) {
			nextBtn.querySelector('span').textContent = 'Continue to Step ' + this.flow.getNextStep() + '';
		} else {
			nextBtn.querySelector('span').textContent = 'All done! Submit your answers';
		}

		this.stepSetup = true;
	}

	resetNextStepNavigation() {
		if (this.stepSetup === false) return;

		const nextBtn = this.root.querySelector("[data-next]");
		nextBtn.setAttribute('disabled', true);

		nextBtn.removeEventListener("click", this.boundNextBtnClickHandler, true);

		nextBtn.querySelector('span').textContent = 'Please complete this step before continuing';

		this.stepSetup = false;
	}

	onOptionClick(stage, step, direction, optionButtons, event) {
		let optionButtonsArr = Array.from(optionButtons);
		const multi = (event.currentTarget.dataset.multi === "false") ? false : true;
		if (multi === false && event.currentTarget.classList.contains('is-selected')) return;
		if (multi === false) optionButtonsArr.forEach((option) => option.classList.remove('is-selected'));

		if (multi === true) {
			event.currentTarget.classList.toggle('is-selected');
		} else {
			event.currentTarget.classList.add('is-selected');
		}

		const stepField = this.stepFields.find(node => node.classList.contains('step-field-' + step));

		if (multi === false) {
			stepField.value = event.currentTarget.dataset.value;
		} else {
			const selectedOptions = optionButtonsArr
				.filter(option => option.classList.contains('is-selected'))
				.map(option => option.dataset.value)
				.join(';');
			stepField.value = selectedOptions;
		}

		if (multi === false) {
			this.currentStepLabels[step] = event.currentTarget.dataset.label;
		} else {
			const selectedLabels = optionButtonsArr
				.filter(option => option.classList.contains('is-selected'))
				.map(option => option.dataset.label)
				.join(', ');
			this.currentStepLabels[step] = selectedLabels;
		}

		stepField.dispatchEvent(new Event("change", { bubbles: true }));
	}

	initiateLoading(stage, step, direction) {
		const panel = stage.querySelector('.machine-finder__panel');
		if (panel.dataset.await) {
			const await = Number(panel.dataset.await);
			const handler = this.loadingAwaitCallback.bind(this);
			setTimeout(handler, await);
		}
	}

	loadingAwaitCallback() {
		this.root.remove();
		const showOnCompleteElms = document.querySelectorAll('.show-on-complete');

		showOnCompleteElms.forEach((elm) => elm.classList.add('is-visible'));

		showOnCompleteElms[0].scrollIntoView();
	}
	
 /**
     * Fetches the results from JSON query
     */
	async fetchJSON() {
		const stepField = document.querySelector('.step-field-' + this.jsonStep);
        const cleanValue = this.escape(stepField.value ?? '');
        const searchURL = this.jsonURL + cleanValue;

        let responseJSON;

        try {
            const response = await fetch(searchURL);

            if (!response.ok) {
                // Handle response error
                throw new Error(`${response.status} ${response.statusText}`);
            }

            responseJSON = await response.json();
            
        } catch (error) {
            if (error instanceof SyntaxError) {
                console.warn('There was a SyntaxError when fetching the JSON', error);
            } else {
                console.warn('There was an error', error);
            }
        }

        if (responseJSON) {
            this.populateLogos(responseJSON);
        }
	}
	
	populateLogos(json) {
		const targetContainer = document.querySelector("[data-logo-target]");
		
		if (!targetContainer) return;
		
		const list = json.list;
		list.forEach((img) => {
			if (!img.src) return;
			
			const imgContainerElm = document.createElement('div');
			const imgElm = document.createElement('img');
			imgElm.src = img.src;
			
			if (img.alt) imgElm.alt = img.alt;
			
			imgContainerElm.appendChild(imgElm);
			
			targetContainer.appendChild(imgContainerElm);
		});
	}
    
}

export { MachineFinder }
